/**
 * SoloSingleFileVideoRecording - Records video+audio directly to a single MP4 file.
 * 
 * Key features:
 * - Uses native MediaRecorder API for reliable background tab recording
 * - File is complete and ready for playback immediately when recording stops
 * - No chunking, no stitching - just a single file
 * - MP4 format with H.264 codec for universal compatibility
 */

// Recording options
export interface SoloSingleFileVideoRecordingOptions {
    trackId: string;
    audioDeviceId?: string;         // specific microphone device ID
    videoDeviceId?: string;         // specific camera device ID
    audioStream?: MediaStream;      // optional pre-processed audio stream
    inputStream?: MediaStream;      // optional existing media stream (e.g. live preview) to avoid re-acquiring devices
    disableAudio?: boolean;         // record video without audio (default: false)
    title?: string;
    projectId?: string;
    width?: number;                 // video width (default: 1280)
    height?: number;                // video height (default: 720)
    videoBitrate?: number;          // video bitrate in bps (default: 4_000_000)
    audioBitrate?: number;          // audio bitrate in bps (default: 128_000)
    autoGainControl?: boolean;      // enable auto gain control (default: true)
    noiseSuppression?: boolean;     // enable browser noise suppression (default: true)
}

// Recording state
type RecordingState = 'idle' | 'recording' | 'stopping' | 'stopped';

const DEFAULT_WIDTH = 1280;
const DEFAULT_HEIGHT = 720;
const DEFAULT_VIDEO_BITRATE = 4_000_000; // 4 Mbps
const DEFAULT_AUDIO_BITRATE = 128_000;   // 128 kbps
const RECORDING_TIMESLICE_MS = 10_000;

// Preferred MIME types in order of preference (MP4 with H.264 for universal compatibility)
const PREFERRED_MIME_TYPES = [
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',  // H.264 Baseline + AAC
    'video/mp4;codecs=avc1.4D401E,mp4a.40.2',  // H.264 Main + AAC
    'video/mp4;codecs=avc1.64001E,mp4a.40.2',  // H.264 High + AAC
    'video/mp4;codecs=h264,aac',
    'video/mp4;codecs=avc1,mp4a',
    'video/mp4',
];

/**
 * Get the best supported MIME type for MediaRecorder
 */
function getSupportedMimeType(): string {
    for (const mimeType of PREFERRED_MIME_TYPES) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
            return mimeType;
        }
    }
    // Fallback - let browser decide
    return '';
}

// OPFS path for solo recordings
const soloVideoRecordingPath = (trackId: string): string =>
    `solo-recordings/${trackId}.mp4`;

/**
 * Get the OPFS root directory.
 */
async function getOpfsRoot(): Promise<FileSystemDirectoryHandle> {
    return await navigator.storage.getDirectory();
}

/**
 * Create a file in OPFS and return a writable stream.
 */
async function createOpfsWritableFile(path: string): Promise<{
    fileHandle: FileSystemFileHandle;
    writable: FileSystemWritableFileStream;
}> {
    const parts = path.split('/').filter(Boolean);
    const fileName = parts.pop()!;

    let dir = await getOpfsRoot();
    for (const segment of parts) {
        dir = await dir.getDirectoryHandle(segment, { create: true });
    }

    const fileHandle = await dir.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();

    return { fileHandle, writable };
}

/**
 * Read a file from OPFS and ensure it has the correct MIME type.
 */
async function readOpfsFile(path: string): Promise<File> {
    const parts = path.split('/').filter(Boolean);
    const fileName = parts.pop()!;

    let dir = await getOpfsRoot();
    for (const segment of parts) {
        dir = await dir.getDirectoryHandle(segment, { create: false });
    }

    const fileHandle = await dir.getFileHandle(fileName, { create: false });
    const file = await fileHandle.getFile();
    
    // OPFS files may not have correct MIME type - ensure it's set for MP4
    if (!file.type || file.type === 'application/octet-stream') {
        // Re-create File with correct MIME type
        const blob = await file.arrayBuffer();
        return new File([blob], fileName, { type: 'video/mp4' });
    }
    
    return file;
}

/**
 * Delete a file from OPFS.
 */
async function deleteOpfsFile(path: string): Promise<void> {
    const parts = path.split('/').filter(Boolean);
    const fileName = parts.pop()!;

    let dir = await getOpfsRoot();
    for (const segment of parts) {
        try {
            dir = await dir.getDirectoryHandle(segment, { create: false });
        } catch {
            return;
        }
    }

    try {
        await dir.removeEntry(fileName);
    } catch {
        // File doesn't exist
    }
}

export class SoloSingleFileVideoRecording {
    private trackId: string;
    private audioDeviceId?: string;
    private videoDeviceId?: string;
    private providedAudioStream?: MediaStream;  // Pre-processed audio stream
    private inputStream?: MediaStream;          // Existing media stream (e.g. live preview)
    private disableAudio: boolean;
    private title?: string;
    private projectId?: string;
    private width: number;
    private height: number;
    private videoBitrate: number;
    private audioBitrate: number;
    private autoGainControl: boolean;
    private noiseSuppression: boolean;

    private state: RecordingState = 'idle';
    private stream: MediaStream | null = null;
    private ownsVideoTracks: boolean = false;  // Whether we created/cloned the video tracks (and should stop them)
    private ownsAudioTracks: boolean = false;  // Whether we created/cloned the audio tracks (and should stop them)
    private mediaRecorder: MediaRecorder | null = null;
    private writable: FileSystemWritableFileStream | null = null;
    private opfsPath: string;
    private chunkCount: number = 0;
    private startTime: number = 0;
    private pendingWrite: Promise<void> = Promise.resolve(); // Track pending OPFS writes

    constructor(options: SoloSingleFileVideoRecordingOptions) {
        this.trackId = options.trackId;
        this.audioDeviceId = options.audioDeviceId;
        this.videoDeviceId = options.videoDeviceId;
        this.providedAudioStream = options.audioStream;
        this.inputStream = options.inputStream;
        this.disableAudio = options.disableAudio ?? false;
        this.title = options.title;
        this.projectId = options.projectId;
        this.width = options.width ?? DEFAULT_WIDTH;
        this.height = options.height ?? DEFAULT_HEIGHT;
        this.videoBitrate = options.videoBitrate ?? DEFAULT_VIDEO_BITRATE;
        this.audioBitrate = options.audioBitrate ?? DEFAULT_AUDIO_BITRATE;
        this.autoGainControl = options.autoGainControl ?? true;
        this.noiseSuppression = options.noiseSuppression ?? true;
        this.opfsPath = soloVideoRecordingPath(this.trackId);
    }

    /**
     * Start recording video and audio.
     */
    public async start(): Promise<void> {
        if (this.state !== 'idle') {
            console.warn('[SoloSingleFileVideoRecording] Cannot start, already recording');
            return;
        }

        console.log('[SoloSingleFileVideoRecording] Starting recording to:', this.opfsPath);

        // 1. Prepare the recording stream.
        // Prefer reusing an existing input stream (e.g. the live preview stream) to avoid
        // re-acquiring the camera. On macOS, double-acquiring the same camera can cause
        // a brief (often ~1s) stall/freeze in the live preview when recording starts.
        if (this.inputStream) {
            const inputVideoTracks = this.inputStream.getVideoTracks();
            if (inputVideoTracks.length === 0) {
                console.warn('[SoloSingleFileVideoRecording] inputStream provided but has no video track; falling back to getUserMedia');
            } else {
                // Use the existing live preview tracks directly instead of cloning.
                // Cloning can still trigger a brief camera pipeline hiccup on start in some browsers.
                const videoTracks = inputVideoTracks;
                this.ownsVideoTracks = false;

                let audioTracks: MediaStreamTrack[] = [];
                if (this.disableAudio) {
                    audioTracks = [];
                    this.ownsAudioTracks = false;
                } else if (this.providedAudioStream) {
                    audioTracks = this.providedAudioStream.getAudioTracks();
                    this.ownsAudioTracks = false;
                    console.log('[SoloSingleFileVideoRecording] Using provided audio stream');
                } else {
                    const inputAudioTracks = this.inputStream.getAudioTracks();
                    if (inputAudioTracks.length > 0) {
                        audioTracks = inputAudioTracks;
                        this.ownsAudioTracks = false;
                    } else {
                        // Best-effort: acquire microphone only (no video) if preview stream has no audio.
                        // This keeps us from re-acquiring the camera, which is the problematic case.
                        try {
                            const audioConstraints: MediaTrackConstraints = this.audioDeviceId
                                ? {
                                      deviceId: { exact: this.audioDeviceId },
                                      autoGainControl: this.autoGainControl,
                                      echoCancellation: this.noiseSuppression,
                                      noiseSuppression: this.noiseSuppression,
                                  }
                                : {
                                      autoGainControl: this.autoGainControl,
                                      echoCancellation: this.noiseSuppression,
                                      noiseSuppression: this.noiseSuppression,
                                  };
                            const audioOnlyStream = await navigator.mediaDevices.getUserMedia({
                                video: false,
                                audio: audioConstraints,
                            });
                            audioTracks = audioOnlyStream.getAudioTracks();
                            this.ownsAudioTracks = true;
                        } catch (err) {
                            console.warn('[SoloSingleFileVideoRecording] Failed to acquire microphone for recording:', err);
                            this.ownsAudioTracks = false;
                        }
                    }
                }

                this.stream = new MediaStream([...videoTracks, ...audioTracks]);
            }
        }

        // Fallback: acquire devices directly.
        if (!this.stream) {
            this.ownsVideoTracks = false;
            this.ownsAudioTracks = false;

        // 1b. Get camera and audio
        if (this.disableAudio) {
            // Video-only recording
            const videoConstraints: MediaStreamConstraints = {
                video: this.videoDeviceId
                    ? {
                        deviceId: { exact: this.videoDeviceId },
                        width: { ideal: this.width },
                        height: { ideal: this.height },
                    }
                    : {
                        width: { ideal: this.width },
                        height: { ideal: this.height },
                    },
                audio: false,
            };

            this.stream = await navigator.mediaDevices.getUserMedia(videoConstraints);
            this.ownsVideoTracks = true;
            this.ownsAudioTracks = false;
        } else if (this.providedAudioStream) {
            // Use pre-processed audio stream
            // Get video separately
            const videoConstraints: MediaStreamConstraints = {
                video: this.videoDeviceId
                    ? {
                        deviceId: { exact: this.videoDeviceId },
                        width: { ideal: this.width },
                        height: { ideal: this.height },
                    }
                    : {
                        width: { ideal: this.width },
                        height: { ideal: this.height },
                    },
                audio: false,
            };
            
            const videoStream = await navigator.mediaDevices.getUserMedia(videoConstraints);
            
            // Combine video tracks with provided audio tracks
            this.stream = new MediaStream([
                ...videoStream.getVideoTracks(),
                ...this.providedAudioStream.getAudioTracks(),
            ]);
            this.ownsVideoTracks = true;
            this.ownsAudioTracks = false;
            console.log('[SoloSingleFileVideoRecording] Using provided audio stream');
        } else {
            // Get camera and microphone access together
            const constraints: MediaStreamConstraints = {
                video: this.videoDeviceId
                    ? {
                        deviceId: { exact: this.videoDeviceId },
                        width: { ideal: this.width },
                        height: { ideal: this.height },
                    }
                    : {
                        width: { ideal: this.width },
                        height: { ideal: this.height },
                    },
                audio: this.audioDeviceId
                    ? {
                        deviceId: { exact: this.audioDeviceId },
                        autoGainControl: this.autoGainControl,
                        echoCancellation: this.noiseSuppression,
                        noiseSuppression: this.noiseSuppression,
                    }
                    : {
                        autoGainControl: this.autoGainControl,
                        echoCancellation: this.noiseSuppression,
                        noiseSuppression: this.noiseSuppression,
                    },
            };

            this.stream = await navigator.mediaDevices.getUserMedia(constraints);
            this.ownsVideoTracks = true;
            this.ownsAudioTracks = true;
        }
        }

        // Log actual track settings
        const videoTrack = this.stream.getVideoTracks()[0];
        const audioTrack = this.stream.getAudioTracks()[0];
        console.log('[SoloSingleFileVideoRecording] Got media stream:', {
            video: videoTrack?.getSettings(),
            audio: audioTrack?.getSettings(),
        });

        // 2. Get supported MIME type
        const mimeType = getSupportedMimeType();
        console.log('[SoloSingleFileVideoRecording] Using MIME type:', mimeType || 'browser default');

        // 3. Create MediaRecorder
        const options: MediaRecorderOptions = {
            videoBitsPerSecond: this.videoBitrate,
            audioBitsPerSecond: this.audioBitrate,
        };
        if (mimeType) {
            options.mimeType = mimeType;
        }

        this.mediaRecorder = new MediaRecorder(this.stream, options);
        this.chunkCount = 0;

        // 4. Create OPFS file for streaming writes
        const { writable } = await createOpfsWritableFile(this.opfsPath);
        this.writable = writable;

        // 5. Handle data available - write directly to OPFS
        // Chain writes to ensure they complete in order and we can wait for them
        this.mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                this.chunkCount += 1;
                
                // Chain the write to pendingWrite to ensure sequential writes
                // and ability to await all writes before closing
                this.pendingWrite = this.pendingWrite.then(async () => {
                    try {
                        await this.writable?.write(event.data);
                    } catch (e) {
                        console.error('[SoloSingleFileVideoRecording] Error writing to OPFS:', e);
                    }
                });

                console.log('[SoloSingleFileVideoRecording] Chunk recorded:', {
                    size: event.data.size,
                    totalChunks: this.chunkCount,
                    isBackground: document.visibilityState === 'hidden',
                });
            }
        };

        // 6. Handle errors
        this.mediaRecorder.onerror = (event) => {
            console.error('[SoloSingleFileVideoRecording] MediaRecorder error:', event);
        };

        // 7. Start recording with a timeslice so we can flush data periodically to OPFS.
        // Fewer chunk boundaries tends to produce more reliable MP4s in Chromium.
        this.startTime = Date.now();
        this.mediaRecorder.start(RECORDING_TIMESLICE_MS);
        this.state = 'recording';

        console.log('[SoloSingleFileVideoRecording] Recording started', {
            trackId: this.trackId,
            videoBitrate: this.videoBitrate,
            audioBitrate: this.audioBitrate,
            mimeType: mimeType || 'default',
        });
    }

    /**
     * Stop recording and finalize the file.
     */
    public async stop(): Promise<{ trackId: string; filePath: string; file: File }> {
        if (this.state !== 'recording') {
            console.warn('[SoloSingleFileVideoRecording] Cannot stop, not recording');
            throw new Error('Not recording');
        }

        this.state = 'stopping';
        const recordingDuration = Date.now() - this.startTime;
        console.log('[SoloSingleFileVideoRecording] Stopping recording...', {
            duration: recordingDuration,
            chunks: this.chunkCount,
        });

        // 1. Flush and stop MediaRecorder (best-effort). `requestData()` helps reduce the chance
        // that the last chunk is lost/truncated when stopping.
        await new Promise<void>((resolve) => {
            if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
                resolve();
                return;
            }

            this.mediaRecorder.onstop = () => {
                console.log('[SoloSingleFileVideoRecording] MediaRecorder stopped');
                resolve();
            };

            try {
                this.mediaRecorder.requestData();
            } catch (err) {
                console.warn('[SoloSingleFileVideoRecording] MediaRecorder.requestData() failed:', err);
            }
            this.mediaRecorder.stop();
        });

        // 2. Stop media stream (only stop audio tracks if we created them)
        if (this.stream) {
            if (this.ownsVideoTracks) {
                this.stream.getVideoTracks().forEach(track => track.stop());
            }
            if (this.ownsAudioTracks) {
                this.stream.getAudioTracks().forEach(track => track.stop());
            }
            this.stream = null;
        }

        // 3. Wait for all pending OPFS writes to complete. Some browsers may deliver a final
        // `dataavailable` after `stop`, so wait until chunk count stabilizes across a tick.
        console.log('[SoloSingleFileVideoRecording] Waiting for pending writes...');
        const waitStart = Date.now();
        const maxWaitMs = 5_000;
        let lastChunkCount = this.chunkCount;
        try {
            while (true) {
                await this.pendingWrite;
                await new Promise<void>((r) => setTimeout(r, 0));
                if (this.chunkCount === lastChunkCount) break;
                lastChunkCount = this.chunkCount;
                if (Date.now() - waitStart > maxWaitMs) {
                    console.warn('[SoloSingleFileVideoRecording] Timed out waiting for final chunk writes', {
                        lastChunkCount,
                        currentChunkCount: this.chunkCount,
                    });
                    break;
                }
            }
        } catch (e) {
            console.error('[SoloSingleFileVideoRecording] Error in pending writes:', e);
        }
        console.log('[SoloSingleFileVideoRecording] All writes complete');

        // 4. Close OPFS writable stream
        if (this.writable) {
            try {
                await this.writable.close();
            } catch (e) {
                console.error('[SoloSingleFileVideoRecording] Error closing writable:', e);
            }
            this.writable = null;
        }

        this.state = 'stopped';
        this.mediaRecorder = null;

        // 5. Read the completed file from OPFS
        const file = await readOpfsFile(this.opfsPath);

        console.log('[SoloSingleFileVideoRecording] Recording complete!', {
            fileSize: file.size,
            fileType: file.type,
            fileName: file.name,
            duration: recordingDuration,
            path: this.opfsPath,
        });

        return {
            trackId: this.trackId,
            filePath: this.opfsPath,
            file,
        };
    }

    /**
     * Pause recording (best-effort). MediaRecorder will stop emitting data while paused.
     */
    public async pause(): Promise<void> {
        if (this.state !== 'recording') return;
        const recorder = this.mediaRecorder;
        if (!recorder) return;
        if (recorder.state !== 'recording') return;
        try {
            recorder.pause();
        } catch (err) {
            console.warn('[SoloSingleFileVideoRecording] Failed to pause:', err);
        }
    }

    /**
     * Resume recording after a pause (best-effort).
     */
    public async resume(): Promise<void> {
        if (this.state !== 'recording') return;
        const recorder = this.mediaRecorder;
        if (!recorder) return;
        if (recorder.state !== 'paused') return;
        try {
            recorder.resume();
        } catch (err) {
            console.warn('[SoloSingleFileVideoRecording] Failed to resume:', err);
        }
    }

    /**
     * Check if the underlying MediaRecorder is paused.
     */
    public isPaused(): boolean {
        return this.mediaRecorder?.state === 'paused';
    }

    /**
     * Check if currently recording.
     */
    public isRecording(): boolean {
        return this.state === 'recording';
    }

    /**
     * Get the track ID.
     */
    public getTrackId(): string {
        return this.trackId;
    }

    /**
     * Get the OPFS path where the file will be saved.
     */
    public getFilePath(): string {
        return this.opfsPath;
    }

    /**
     * Clean up the recording file.
     */
    public async cleanup(): Promise<void> {
        if (this.state === 'recording') {
            try {
                await this.stop();
            } catch { }
        }

        await deleteOpfsFile(this.opfsPath);
        console.log('[SoloSingleFileVideoRecording] Cleaned up:', this.opfsPath);
    }
}

// Generate a unique track ID for solo recordings
export function generateSoloTrackId(): string {
    return `solo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Export utility functions
export { readOpfsFile as readVideoOpfsFile, deleteOpfsFile as deleteVideoOpfsFile, soloVideoRecordingPath };
